import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { importCsv } from "../api/boardApi";
import { errorToast, successToast } from "../../../redux/actionCreator";

// Minimal CSV import — paste text or pick a file. The backend matches
// headers to column titles case-insensitively, so the user can paste a
// spreadsheet directly. Group column (optional) routes rows into board
// groups by title; otherwise everything goes into the chosen target group.
export default function CsvImportModal({ board, onClose, onImported }) {
  const [csv, setCsv] = useState("");
  const [groupId, setGroupId] = useState(board?.groups?.[0]?._id || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsv(text);
  };

  const handleImport = async () => {
    if (!csv.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await importCsv(board._id, csv, groupId);
      setResult(res);
      successToast(res.message);
      onImported && onImported();
    } catch (e) {
      errorToast(e.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Import CSV — {board.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="muted small">
          Paste CSV below or pick a file. Header row must include a <code>Title</code> column.
          Other headers will be matched to your board's columns by title (case-insensitive).
          A <code>Group</code> column routes rows into existing groups by name; otherwise rows
          go to the target group you pick below.
        </p>

        <div className="csv-controls">
          <Form.Group className="mb-2">
            <Form.Label>Default target group</Form.Label>
            <Form.Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {(board.groups || []).map((g) => (
                <option key={g._id} value={g._id}>{g.title}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Or upload a .csv</Form.Label>
            <Form.Control type="file" accept=".csv,text/csv" onChange={handleFile} />
          </Form.Group>
        </div>

        <Form.Group className="mb-2">
          <Form.Label>CSV content</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"Title,Type,Priority,Status\nFix login bug,Bug,High,To do\nAdd SSO,Feature,Medium,Working on it"}
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
        </Form.Group>

        {result && (
          <div className="csv-result">
            <strong>{result.message}</strong>
            {result.data?.errors?.length > 0 && (
              <ul className="csv-errors">
                {result.data.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        <Button variant="primary" disabled={busy || !csv.trim()} onClick={handleImport}>
          {busy ? <><Spinner size="sm" animation="border" /> Importing…</> : "Import"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
